import { DesignData, MediaText } from 'editor/src/store/design/types';
import {
  getDriveableSharedTextFieldIds,
  getPersonalizationTextUuids,
  receivesSharedValue,
} from 'editor/src/store/design/util/sharedTextFields';
import { matchesValueFormat } from 'editor/src/store/design/util/valueRegex';

export interface ValueFormatMismatch {
  uuid: number;
  spreadIndex: number;
}

/**
 * Every personalisable text whose current value fails its own regex forced
 * format — the full editor's half of the shared validation. `getPersonalizationErrors`
 * (product-personalizer) states the buyer-facing side of the same rule; the two
 * call the same `matchesValueFormat` so they can never drift apart.
 *
 * A text counts only when the buyer could actually have produced its value:
 * - `hidden`: a collapsed reflow row or an inactive Condition option. It prints
 *   nothing, so a mismatch on it has nowhere to be shown or fixed.
 * - `personalizationLocked`: not the buyer's to fill; no panel offers it a field.
 * - `sample`: the template's own placeholder, never typed by anyone. Until the
 *   buyer replaces it, its text is the author's, not a value to hold to the
 *   author's own pattern.
 * - `variableColumn`: printed from a code-set column per unit; there is no
 *   input, and its text is only the column's sample.
 * - otherwise offered a field on its own spread, or driven by a shared-field
 *   group that has a field somewhere — the same notion `getPersonalizationErrors`
 *   and `resolveSharedTextField` use, so a rule shown to the buyer is exactly
 *   the rule enforced here.
 */
function getValueFormatMismatches(designData: DesignData | undefined): ValueFormatMismatch[] {
  const mismatches: ValueFormatMismatch[] = [];
  if (!designData) {
    return mismatches;
  }

  const driveableGroups = getDriveableSharedTextFieldIds(designData);

  designData.spreads.forEach((spread, spreadIndex) => {
    const offered = getPersonalizationTextUuids(spread);

    spread.pages.forEach((page) =>
      page.groups.media?.forEach((element) => {
        if (element.type !== 'text' || element.hidden || element.personalizationLocked || element.sample) {
          return;
        }

        const textElement = element as MediaText;
        if (textElement.extra.variableColumn) {
          return;
        }

        const drivenByGroup =
          receivesSharedValue(textElement) && driveableGroups.has(textElement.extra.sharedFieldId ?? '');
        if (!offered.has(element.uuid) && !drivenByGroup) {
          return;
        }

        if (!matchesValueFormat(textElement.extra.text, textElement.extra.valueFormat)) {
          mismatches.push({ uuid: element.uuid, spreadIndex });
        }
      }),
    );
  });

  return mismatches;
}

export default getValueFormatMismatches;
